import React, { useEffect, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import { down } from 'styled-breakpoints'
import { CONFETTI_SVG } from './confettiSvg'

/** Sayfa yenilenene kadar confetti yalnızca bir kez oynar (SPA remount’larında tekrar etmez). */
let confettiPlayedAt = 0
const CONFETTI_DURATION_MS = 3000
const STRICT_MODE_REMOUNT_GRACE_MS = 500

const Band = styled.div<{ $position: 'top' | 'bottom' }>`
  position: absolute;
  left: 50%;
  width: min(100%, 1203px);
  pointer-events: none;
  line-height: 0;
  overflow: hidden;
  transform: translateX(-50%);

  ${({ $position }) =>
    $position === 'top'
      ? css`
          top: 0;
        `
      : css`
          top: 50%;
        `}

  svg {
    width: 100%;
    height: auto;
    display: block;
    overflow: hidden;

    /* Figma export’u infinite; kampanya confetti’si tek sefer oynasın */
    * {
      animation-iteration-count: 1 !important;
    }
  }

  /* Mobilde %100 ölçek parçaları küçültür; native genişlikte tut, ortala, taşanı kırp */
  ${down('md')} {
    width: 1203px;
  }
`

const isHexColorId = (id: string) => /^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$|^[0-9A-Fa-f]{8}$/.test(id)

const prefixSvgIds = (svg: string, prefix: string) =>
  svg
    .replaceAll(/\bid="([^"]+)"/g, (_m, id: string) => `id="${prefix}${id}"`)
    .replaceAll(/#([A-Za-z_][\w-]*)/g, (match, id: string) => (isHexColorId(id) ? match : `#${prefix}${id}`))
    .replaceAll(/svg\s*\{\s*overflow:\s*visible;\s*\}/g, 'svg { overflow: hidden; }')
    .replaceAll(/<svg\b([^>]*)>/g, (_m, attrs: string) => {
      const cleaned = String(attrs).replaceAll(/\soverflow="[^"]*"/g, '')
      return `<svg${cleaned} overflow="hidden">`
    })

type ConfettiDecorationProps = {
  position: 'top' | 'bottom'
}

const ConfettiDecoration: React.FC<ConfettiDecorationProps> = ({ position }) => {
  const markup = useMemo(() => prefixSvgIds(CONFETTI_SVG, `confetti-${position}-`), [position])

  return <Band $position={position} data-testid={`promoted-flight-confetti-${position}`} aria-hidden="true" dangerouslySetInnerHTML={{ __html: markup }} />
}

type ConfettiOnceProps = {
  enabled: boolean
  children: React.ReactNode
}

/** Confetti’yi bu sayfa yüklemesinde yalnızca bir kez mount eder; refresh’e kadar tekrarlamaz. */
export const ConfettiOnce: React.FC<ConfettiOnceProps> = ({ enabled, children }) => {
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (!enabled) return

    const now = Date.now()
    if (confettiPlayedAt && now - confettiPlayedAt > STRICT_MODE_REMOUNT_GRACE_MS) {
      return
    }

    if (!confettiPlayedAt) {
      confettiPlayedAt = now
    }

    setShow(true)
    const hideTimer = globalThis.setTimeout(() => setShow(false), CONFETTI_DURATION_MS)
    return () => globalThis.clearTimeout(hideTimer)
  }, [enabled])

  if (!show) return null
  return <>{children}</>
}

export default ConfettiDecoration
